import store from 'store'
import { stringify } from 'query-string'

/**
 * Integrasi web push Firebase Cloud Messaging (FCM).
 *
 * Modul ini sengaja murni (Firebase + storage, TANPA axios/apis) karena
 * `utils/axios` memanggil `teardownPushNotification()` saat sesi berakhir;
 * mengimpor `utils/apis` di sini akan membuat circular import. Registrasi token
 * ke BE ada di `layouts/Main/PushNotificationSync`.
 *
 * SDK Firebase dimuat lewat `import()` dinamis supaya tidak membebani bundle
 * utama — user yang belum/tidak mengaktifkan push tidak pernah mengunduhnya.
 */

/** Path service worker FCM (file statis di `public/`). */
export const PUSH_SW_PATH = '/firebase-messaging-sw.js'

/**
 * Scope default Firebase. Dipakai eksplisit agar `getToken` & `deleteToken`
 * selalu merujuk registration yang sama dan tidak bentrok dengan SW lain.
 */
export const PUSH_SW_SCOPE = '/firebase-cloud-messaging-push-scope'

/**
 * Masa berlaku penanda registrasi. BE menghapus token yang tidak didaftarkan
 * ulang > 60 hari, jadi token cukup di-refresh sekali sehari; penanda ini juga
 * mencegah POST ganda saat user membuka banyak tab.
 */
export const PUSH_REGISTRATION_TTL_MS = 24 * 60 * 60 * 1000

/** Lama banner ajakan disembunyikan setelah user memilih "Nanti". */
export const PUSH_PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Event window untuk menyinkronkan state push antar-komponen di tab yang sama
 * (banner, card pengaturan, dan sinkronisasi token di layout).
 */
export const PUSH_STATE_EVENT = 'kledo:push-state-change'

/**
 * Pesan dari service worker ke tab saat notifikasi diklik. Tab yang sudah
 * terbuka dipindahkan halamannya lewat router, bukan dibuka ulang.
 * Harus sama dengan konstanta bernama sama di public/firebase-messaging-sw.js
 * (dijaga test src/utils/pushNotification.test.js).
 */
export const PUSH_NAVIGATE_MESSAGE = 'kledo:push-navigate'

// Prefix `push.` wajib: key `device_token` sudah dipakai `utils/cookies`
// (token device login), BUKAN token FCM.
export const PUSH_STORAGE_KEYS = {
  token: 'push.fcm_token',
  registration: 'push.registration',
  promptDismissedAt: 'push.prompt_dismissed_at',
  optOut: 'push.opt_out',
}

/**
 * Ambil konfigurasi Firebase dari env.
 * @returns {{config: object, vapidKey: string}|null} Null bila ada env yang
 *   kosong — web push dianggap nonaktif di environment tsb.
 */
export const getFirebaseConfig = () => {
  // Akses per key (bukan `const env = import.meta.env`) agar Vite mengganti
  // nilainya secara statis, bukan meng-inline seluruh objek env ke bundle.
  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  if (!vapidKey || Object.values(config).some((value) => !value)) {
    return null
  }
  return { config, vapidKey }
}

/**
 * Beri tahu semua pemakai `usePushPermission` di tab ini bahwa state push
 * (izin, opt-out, dismiss banner) berubah.
 */
export const notifyPushStateChange = () => {
  window.dispatchEvent(new Event(PUSH_STATE_EVENT))
}

/**
 * @returns {"default"|"granted"|"denied"} Izin notifikasi browser saat ini.
 */
export const getNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied'
  }
  return window.Notification.permission
}

/**
 * Cek apakah web push bisa dipakai di browser & environment ini.
 * iOS Safari di luar PWA (belum "Tambahkan ke Layar Utama") dan in-app
 * browser umumnya tidak lolos cek `isSupported()` Firebase.
 * @returns {Promise<boolean>} True bila didukung.
 */
export const isPushSupported = async () => {
  if (!getFirebaseConfig()) {
    return false
  }
  if (typeof window === 'undefined' || !window.isSecureContext) {
    return false
  }
  if (
    !('serviceWorker' in navigator) ||
    !('Notification' in window) ||
    !('PushManager' in window)
  ) {
    return false
  }
  try {
    const { isSupported } = await import('firebase/messaging')
    return await isSupported()
  } catch {
    return false
  }
}

let messagingPromise = null

/**
 * Inisialisasi Firebase app + Messaging satu kali (lazy, di-cache).
 * @returns {Promise<{messaging: object, sdk: object}>} Instance messaging dan
 *   modul `firebase/messaging`.
 */
const loadMessaging = () => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const [{ initializeApp, getApps, getApp }, sdk] = await Promise.all([
        import('firebase/app'),
        import('firebase/messaging'),
      ])
      const app = getApps().length ? getApp() : initializeApp(getFirebaseConfig().config)
      return { messaging: sdk.getMessaging(app), sdk }
    })().catch((error) => {
      // Jangan cache kegagalan (mis. chunk gagal diunduh) agar bisa dicoba lagi.
      messagingPromise = null
      throw error
    })
  }
  return messagingPromise
}

/**
 * Daftarkan service worker FCM. Config dikirim lewat query string karena SW
 * tidak bisa membaca `import.meta.env`; `stringify` mengurutkan key sehingga URL
 * stabil dan browser tidak meng-install ulang SW di tiap app start.
 * @returns {Promise<ServiceWorkerRegistration>} Registration SW FCM.
 */
const registerMessagingServiceWorker = () => {
  const { config } = getFirebaseConfig()
  return navigator.serviceWorker.register(`${PUSH_SW_PATH}?${stringify(config)}`, {
    scope: PUSH_SW_SCOPE,
  })
}

/**
 * Ambil token FCM perangkat ini (membuat subscription bila belum ada) dan
 * simpan ke storage supaya bisa dikirim saat logout. Wajib izin `granted`.
 * @returns {Promise<string|null>} Token FCM.
 */
export const fetchFcmToken = async () => {
  const { messaging, sdk } = await loadMessaging()
  const serviceWorkerRegistration = await registerMessagingServiceWorker()
  const token = await sdk.getToken(messaging, {
    vapidKey: getFirebaseConfig().vapidKey,
    serviceWorkerRegistration,
  })
  if (token) {
    store.set(PUSH_STORAGE_KEYS.token, token)
  }
  return token || null
}

/**
 * @returns {string|null} Token FCM terakhir yang tersimpan di perangkat ini.
 */
export const getStoredFcmToken = () => store.get(PUSH_STORAGE_KEYS.token) || null

/**
 * Tentukan apakah token perlu (ulang) dikirim ke BE.
 * @param {object} params - Parameter.
 * @param {string} params.token - Token FCM saat ini.
 * @param {string|number} params.userId - ID user yang login.
 * @param {number} [params.now=Date.now()] - Waktu sekarang (untuk test).
 * @returns {boolean} True bila token/user berganti atau penanda kedaluwarsa.
 */
export const shouldRegisterPushToken = ({ token, userId, now = Date.now() }) => {
  const registration = store.get(PUSH_STORAGE_KEYS.registration)
  if (!registration) {
    return true
  }
  return (
    registration.token !== token ||
    registration.userId !== String(userId) ||
    !(now - registration.at < PUSH_REGISTRATION_TTL_MS)
  )
}

/**
 * Catat bahwa token sudah tersimpan di BE untuk user ini.
 * @param {object} params - Parameter.
 * @param {string} params.token - Token FCM.
 * @param {string|number} params.userId - ID user.
 */
export const markPushTokenRegistered = ({ token, userId }) => {
  store.set(PUSH_STORAGE_KEYS.registration, {
    token,
    userId: String(userId),
    at: Date.now(),
  })
}

/**
 * Putus perangkat ini dari push: hapus token di FCM (`deleteToken`) lalu
 * bersihkan storage. Dipanggil di jalur sesi berakhir — termasuk sesi habis
 * yang tidak sempat memanggil API logout — agar komputer bersama tidak terus
 * menerima push milik akun sebelumnya. Token yang sudah dihapus dibalas
 * UNREGISTERED oleh FCM dan dibersihkan BE otomatis.
 * Best-effort: tidak pernah melempar error.
 * @returns {Promise<void>}
 */
export const teardownPushNotification = async () => {
  const hadToken = !!getStoredFcmToken()
  store.remove(PUSH_STORAGE_KEYS.token)
  store.remove(PUSH_STORAGE_KEYS.registration)
  if (!hadToken || !getFirebaseConfig()) {
    return
  }
  if (getNotificationPermission() !== 'granted') {
    return
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(PUSH_SW_SCOPE)
    if (!registration) {
      return
    }
    const { messaging, sdk } = await loadMessaging()
    // `deleteToken` tanpa registration akan mendaftarkan SW default TANPA query
    // config dan menimpa SW kita. `getToken` dengan registration yang ada
    // mengikat registration tsb ke instance messaging (token lama dikembalikan
    // dari cache, tidak membuat subscription baru) sebelum token dihapus.
    await sdk.getToken(messaging, {
      vapidKey: getFirebaseConfig().vapidKey,
      serviceWorkerRegistration: registration,
    })
    await sdk.deleteToken(messaging)
  } catch (error) {
    console.warn('Push notification teardown failed:', error)
  }
}

/**
 * @returns {boolean} True bila user menonaktifkan push di perangkat ini.
 */
export const isPushOptedOut = () => !!store.get(PUSH_STORAGE_KEYS.optOut)

/**
 * @param {boolean} optedOut - Status opt-out push di perangkat ini.
 */
export const setPushOptOut = (optedOut) => {
  if (optedOut) {
    store.set(PUSH_STORAGE_KEYS.optOut, true)
  } else {
    store.remove(PUSH_STORAGE_KEYS.optOut)
  }
}

/**
 * @param {number} [now=Date.now()] - Waktu sekarang (untuk test).
 * @returns {boolean} True bila banner ajakan masih dalam masa "Nanti".
 */
export const isPushPromptDismissed = (now = Date.now()) => {
  const dismissedAt = store.get(PUSH_STORAGE_KEYS.promptDismissedAt)
  return !!dismissedAt && now - dismissedAt < PUSH_PROMPT_SNOOZE_MS
}

/** Sembunyikan banner ajakan selama `PUSH_PROMPT_SNOOZE_MS`. */
export const dismissPushPrompt = () => {
  store.set(PUSH_STORAGE_KEYS.promptDismissedAt, Date.now())
}

/**
 * Lepas service worker OneSignal lama (`/sdk/OneSignalSDKWorker.js`) yang masih
 * terpasang di browser user. Selama transisi BE mengirim push ke OneSignal DAN
 * FCM; melepas SW-nya ikut mematikan subscription push OneSignal di browser ini
 * sehingga notifikasi tidak masuk dua kali.
 *
 * Hanya dijalankan bila FCM aktif (config Firebase terisi): tanpa FCM, SW
 * OneSignal yang tersisa tetap mengantar notifikasi seperti sebelumnya.
 * @returns {Promise<void>}
 */
export const unregisterLegacyOneSignalWorkers = async () => {
  if (!getFirebaseConfig()) {
    return
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registrations
        .filter((registration) => {
          const worker = registration.active || registration.waiting || registration.installing
          return /OneSignalSDK/i.test(worker?.scriptURL || '')
        })
        .map((registration) => registration.unregister()),
    )
  } catch {
    // best-effort cleanup
  }
}

/**
 * Langganan pesan FCM saat ada tab Kledo yang terlihat (foreground). Di
 * kondisi ini SDK TIDAK menampilkan notifikasi otomatis.
 * @param {(payload: object) => void} callback - Handler payload FCM.
 * @returns {Promise<() => void>} Fungsi unsubscribe.
 */
export const subscribeForegroundMessages = async (callback) => {
  const { messaging, sdk } = await loadMessaging()
  return sdk.onMessage(messaging, callback)
}

/**
 * Normalisasi payload FCM foreground ke bentuk yang dipakai UI. BE Kledo tidak
 * mengirim `tag`, jadi `key` memakai `messageId` — pesan yang sama yang
 * diterima beberapa tab tetap ter-dedupe.
 * @param {object} payload - Payload `onMessage`.
 * @returns {{key: string, title: string, body: string, link: string, data: object}} Pesan.
 *   `data` diteruskan apa adanya untuk menyusun link detail transaksi.
 */
export const toForegroundMessage = (payload = {}) => ({
  key: payload.messageId || '',
  title: payload.notification?.title || '',
  body: payload.notification?.body || '',
  link: payload.fcmOptions?.link || payload.data?.web_url || '',
  data: payload.data || {},
})

/**
 * Ubah link notifikasi dari BE (mis. `https://app.kledo.com/#/finance-approval`)
 * menjadi path hash router untuk navigasi di dalam app.
 * @param {string} link - `web_url` / `fcm_options.link`.
 * @returns {string|null} Path (mis. `/finance-approval`), null bila link kosong.
 */
export const getPushLinkPath = (link) => {
  if (!link) {
    return null
  }
  try {
    // Base hanya untuk mem-parse link relatif; yang dipakai cuma hash-nya.
    return new URL(link, 'http://localhost').hash.replace(/^#/, '') || '/'
  } catch {
    return null
  }
}

/**
 * Tampilkan notifikasi sistem untuk pesan foreground (tab terlihat tapi tidak
 * sedang fokus). Klik ditangani `notificationclick` di SW FCM.
 * @param {{key: string, title: string, body: string, link: string, data: object}} message -
 *   Hasil `toForegroundMessage`.
 * @returns {Promise<void>}
 */
export const showSystemNotification = async ({ key, title, body, link, data }) => {
  try {
    const registration = await navigator.serviceWorker.getRegistration(PUSH_SW_SCOPE)
    if (!registration) {
      return
    }
    await registration.showNotification(title, {
      body,
      tag: key || undefined,
      icon: '/icon-192.png',
      // `pushData` dibaca SW saat diklik, untuk link detail transaksi.
      data: { link, pushData: data },
    })
  } catch (error) {
    console.warn('Push notification display failed:', error)
  }
}
